import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DOC_DRAG } from '../constants';
import { DocIcon, isDefaultDocIcon } from '../../../Sidebar/icons';
import { renderIcon } from '../../../SprintDetail/FlowchartView/utils/renderIcon';
import { copyDocMenuLabel, copyDocToClipboard } from '../../../../utils/docClipboard';
import { estimatePeekSize, placeAtlasPeek } from '../utils/placeAtlasPeek';
import './AtlasPeek.css';

const isAppIcon = (icon) =>
  icon && typeof icon === 'object' && (icon.type === 'fontawesome' || icon.type === 'custom');

const PeekDocGlyph = ({ icon }) => {
  if (isDefaultDocIcon(icon)) {
    return <DocIcon size={14} />;
  }
  if (isAppIcon(icon)) {
    return renderIcon(icon, { size: '14px', fallback: <DocIcon size={14} /> });
  }
  if (typeof icon === 'object' && icon.type === 'emoji' && icon.value) {
    return <span className="atlas__peek-chip-glyph">{icon.value}</span>;
  }
  if (typeof icon === 'string') {
    return <span className="atlas__peek-chip-glyph">{icon}</span>;
  }
  return <DocIcon size={14} />;
};

/** Same diagonal “open out” mark as the mission open-in-view control. */
const OpenOutIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const AtlasPeek = ({
  peek,
  folder,
  others,
  viewport,
  originX = 0,
  originY = 0,
  useDemo,
  onOpenDoc,
  onOpenDocInNewTab,
  onBrowseFolder,
}) => {
  const cardRef = useRef(null);
  const [menu, setMenu] = useState(null);
  const [peekSize, setPeekSize] = useState(() => estimatePeekSize(peek));

  useLayoutEffect(() => {
    setPeekSize(estimatePeekSize(peek));
  }, [peek]);

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    if (Math.abs(w - peekSize.w) > 1 || Math.abs(h - peekSize.h) > 1) {
      setPeekSize({ w, h });
    }
  }, [peek, peekSize.w, peekSize.h, folder, viewport]);

  const placed = useMemo(() => {
    if (!folder || !viewport) {
      return { x: folder ? folder.x + folder.width + 12 : 0, y: folder?.y || 0 };
    }
    return placeAtlasPeek({ folder, others, peekSize, viewport });
  }, [folder, others, peekSize, viewport]);

  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const timer = window.setTimeout(() => {
      document.addEventListener('click', close);
      document.addEventListener('contextmenu', close);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('click', close);
      document.removeEventListener('contextmenu', close);
    };
  }, [menu]);

  const openDocMenu = (e, doc) => {
    e.preventDefault();
    e.stopPropagation();
    if (useDemo) return;
    setMenu({ x: e.clientX, y: e.clientY, doc });
  };

  return (
    <div
      ref={cardRef}
      data-atlas-no-pan
      className="atlas__peek"
      style={{ left: placed.x - originX, top: placed.y - originY }}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {peek.empty ? (
        <p className="atlas__peek-label">
          {peek.kind === 'unfiled' ? 'No loose docs' : 'Empty folder'}
        </p>
      ) : (
        peek.groups.map((g) => (
          <div key={g.key} className="atlas__peek-group">
            <div className="atlas__peek-label">{g.label}</div>
            {g.shown.map((doc) => (
              <button
                key={doc.id}
                type="button"
                className="atlas__peek-chip"
                draggable={!useDemo}
                onDragStart={(e) => {
                  e.dataTransfer.setData(DOC_DRAG, doc.id);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onClick={() => onOpenDoc?.(doc.id)}
                onMouseDown={(e) => {
                  if (e.button !== 1) return;
                  e.preventDefault();
                  e.stopPropagation();
                  onOpenDocInNewTab?.(doc.id);
                }}
                onContextMenu={(e) => openDocMenu(e, doc)}
              >
                <span className="atlas__peek-chip-icon">
                  <PeekDocGlyph icon={doc.icon} />
                </span>
                <span className="atlas__peek-chip-title">{doc.title || 'Untitled'}</span>
              </button>
            ))}
            {g.more > 0 && (
              <button
                type="button"
                className="atlas__peek-more"
                onClick={() => onBrowseFolder?.(peek.browseFolderId)}
              >
                +{g.more} more
              </button>
            )}
          </div>
        ))
      )}
      {onBrowseFolder && (
        <button
          type="button"
          className="atlas__peek-browse"
          onClick={() => onBrowseFolder(peek.browseFolderId)}
        >
          <OpenOutIcon />
          Browse
        </button>
      )}

      {menu &&
        createPortal(
          <div
            className="atlas__ctx"
            style={{ left: menu.x, top: menu.y }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onOpenDocInNewTab?.(menu.doc.id);
                setMenu(null);
              }}
            >
              Open in new tab
            </button>
            <button
              type="button"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                copyDocToClipboard({ id: menu.doc.id, title: menu.doc.title });
                setMenu(null);
              }}
            >
              {copyDocMenuLabel(menu.doc.title)}
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AtlasPeek;
